import React from 'react'
import { Map } from 'react-leaflet'
import { inject, observer } from 'mobx-react'
import { latLng, latLngBounds, Point } from 'leaflet'
import { dispatchKeyPressEvent, getPropByRegex, loadScript } from '../../utils'
import _ from 'lodash'
import 'leaflet-google-places-autocomplete'
import SearchErrorTooltip from '../common/tooltips/SearchErrorTooltip'
import { keyCodes } from '../../constants'

// load google maps scripts
loadScript(`https://maps.googleapis.com/maps/api/js?key=${process.env.GOOGLE_MAPS_KEY}&libraries=places`)

@inject('uiStore', 'selectionStore')
@observer
class CustomMap extends React.Component {
  mapRef = React.createRef()
  state = {
    savedBounds: null,
    foundLocation: null,
    isHoverLoading: false
  }

  _hoverRequestId = 0
  _wasDragging = false

  setupSearchControl (leafletElement) {
    const { uiStore } = this.props

    const searchErrorSelector = 'input.leaflet-gac-control'

    const searchControl = new window.L.Control.GPlaceAutocomplete({
      callback: place => {
        if (!place.geometry) {
          if (place.name === '') {
            return false
          }

          // get predictions object
          const gmPlace = _.get(
            searchControl.autocomplete,
            'gm_accessors_.place'
          )
          const predictionsObj = gmPlace && getPropByRegex(gmPlace, /^[a-zA-Z]d$/)

          if (_.get(predictionsObj, 'predictions.length') > 0) {
            dispatchKeyPressEvent(40, 'ArrowDown', searchControl.searchBox)
            dispatchKeyPressEvent(13, 'Enter', searchControl.searchBox)
            return false
          }

          this.setState({ savedBounds: null })
          uiStore.openClickTooltip({
            style: {
              width: 210
            },
            selector: searchErrorSelector,
            text: (<SearchErrorTooltip />)
          })
        } else {
          const viewport = place.geometry.viewport
          const lat = place.geometry.location.lat()
          const lng = place.geometry.location.lng()
          const location = Object.entries(viewport)
          const location1 = Object.values(location[1][1])
          const location2 = Object.values(location[0][1])
          const bounds = [
            [location2[1], location1[1]],
            [location2[0], location1[1]]
          ]
          const foundLocation = [lat, lng]

          this.setState({ savedBounds: bounds, foundLocation })
          uiStore.setBounds(bounds)
          uiStore.setFoundLocation(foundLocation)
          searchControl.searchBox.blur()
        }
        return false
      }
    })

    searchControl.searchBox.addEventListener('keydown', e => {
      e.key && _.defer(() => {
        const key = e.key.toLowerCase()
        if (key === 'enter' &&
          searchControl.searchBox.value !== '' &&
          _.get(this, 'state.savedBounds')
        ) {
          uiStore.setBounds(this.state.savedBounds)
          uiStore.setFoundLocation(this.state.foundLocation)
        }
      })

      if (uiStore.clickTooltipOptions.selector === searchErrorSelector) {
        uiStore.hideClickTooltip()
      }
    })

    return searchControl
  }

  panMap = (e, leafletElement) => {
    let lat, lng
    try {
      // catch errors if map not initialized yet
      lat = leafletElement.getCenter().lat
      lng = leafletElement.getCenter().lng
    } catch (e) {
      return
    }

    const step = 20
    const point = leafletElement.project([lat, lng])

    switch (e.key) {
      case keyCodes.ARROW_LEFT:
        point.x -= step
        break
      case keyCodes.ARROW_UP:
        point.y -= step
        break
      case keyCodes.ARROW_RIGHT:
        point.x += step
        break
      case keyCodes.ARROW_DOWN:
        point.y += step
        break
      default:
        break
    }

    const res = leafletElement.unproject(point)
    leafletElement.panTo(new window.L.LatLng(res.lat, res.lng))
  }

  componentDidMount () {
    const leafletElement = this.mapRef.current && this.mapRef.current.leafletElement
    if (!leafletElement) return

    const { uiStore } = this.props
    uiStore.map = this.mapRef.current

    const southWest = latLng(-89.98155760646617, -360)
    const northEast = latLng(89.99346179538875, 360)
    const bounds = latLngBounds(southWest, northEast)

    // zoomSnap={0} allows fractional zoom. Preserve it when a close marker
    // viewport is reused by a newly mounted map, including the print map.
    if (!Number.isFinite(uiStore.viewport.zoom)) {
      uiStore.viewport.zoom = 1
      leafletElement._zoom = uiStore.viewport.zoom
    }

    leafletElement.setMaxBounds(bounds)
    leafletElement.setMinZoom(2)

    leafletElement.on('drag', () => {
      leafletElement.panInsideBounds(bounds, { animate: false })
    })

    // Disable rounding of geometry to integers
    // https://github.com/Leaflet/Leaflet/pull/4808/files
    leafletElement.latLngToLayerPoint = function (latlng) {
      const projectedPoint = this.project(window.L.latLng(latlng))
      return projectedPoint._subtract(this.getPixelOrigin())
    }

    // The map element takes up the whole window's width and height,
    // but parts of it are covered: on the left by tools, and on the bottom
    // by Analysis. So zoom about the center of the visible map, rather
    // than the center of full-window map element.
    leafletElement.setZoom = (zoom, options) => {
      const { paddingTopLeft, paddingBottomRight } = this.props.boundsOptions
      const size = leafletElement.getSize()

      const center = new Point(
        (paddingTopLeft.x + (size.x - paddingBottomRight.x)) / 2,
        (paddingTopLeft.y + (size.y - paddingBottomRight.y)) / 2
      )

      return leafletElement.setZoomAround(center, zoom, options)
    }

    // Pan map using arrow keys
    this._keydownHandler = e => {
      if (uiStore.allowPanMap) {
        this.panMap(e, leafletElement)
      }
    }
    document.addEventListener('keydown', this._keydownHandler)

    // Track if map was dragged by the user to show "Search this area" button
    // IMPORTANT: zoom in/out should NOT trigger this button.
    leafletElement.on('dragstart', () => {
      this._wasDragging = true
    })
    leafletElement.on('zoomstart', () => {
      this._wasDragging = false
    })

    // Listen for map movement to show "Search this area" button for POI searches
    // Show button only when the user actually DRAGGED the map (not zoom, not programmatic moves).
    this._mapMoveEndHandler = _.debounce(() => {
      // If the map just moved programmatically (e.g. fitBounds after a search),
      // do NOT show "Search this area" until the user actually moves the map again.
      if (uiStore.poiIgnoreMoveEndUntil && Date.now() < uiStore.poiIgnoreMoveEndUntil) {
        uiStore.showSearchThisAreaButton = false
        return
      }
      // Show button for both text search and nearby search
      const hasActiveSearch = (uiStore.poiSearchQuery && uiStore.poiSearchQuery.trim()) || uiStore.isNearbySearchActive
      if (uiStore.poiSearchOriginalBounds && hasActiveSearch && this._wasDragging) {
        const currentBounds = leafletElement.getBounds()
        if (currentBounds && currentBounds.isValid() && uiStore.poiSearchOriginalBounds.isValid()) {
          // Check if map center has moved significantly
          const currentCenter = currentBounds.getCenter()
          const originalCenter = uiStore.poiSearchOriginalBounds.getCenter()

          // Use original bounds size for threshold (more stable than current size)
          const originalSize = uiStore.poiSearchOriginalBounds.getNorthEast().distanceTo(uiStore.poiSearchOriginalBounds.getSouthWest())
          const centerDistance = currentCenter.distanceTo(originalCenter)

          // Show button if center moved more than 3% of the original search bounds size
          // Using original size makes threshold more consistent across zoom levels
          // Very small threshold (100 meters) to show button quickly on any drag
          // But add a small tolerance (50m) to account for rounding differences after new search
          const threshold = Math.max(originalSize * 0.03, 100)
          const tolerance = 50 // Small tolerance to prevent button from showing immediately after new search
          uiStore.showSearchThisAreaButton = centerDistance > (threshold + tolerance)
        }
      } else {
        // If there is no active search, always hide the button.
        // If user did NOT drag (e.g. zoom in/out), do NOT hide an already-visible button.
        // This keeps the button stable once shown, without letting zoom create it.
        if (!hasActiveSearch) {
          uiStore.showSearchThisAreaButton = false
        }
      }
      // Reset drag flag after handling moveend
      this._wasDragging = false
    }, 100) // 100ms debounce to prevent rapid updates during drag/zoom

    this._contextmenuHandler = e => {
      if (e && e.originalEvent) {
        e.originalEvent.preventDefault()
      }
      if (e && e.latlng) {
        uiStore.openQuickCreate(e.latlng)
      }
    }
    leafletElement.on('contextmenu', this._contextmenuHandler)
    leafletElement.on('moveend', this._mapMoveEndHandler)
  }

  componentWillUnmount () {
    if (this._contextmenuHandler && this.mapRef.current && this.mapRef.current.leafletElement) {
      this.mapRef.current.leafletElement.off('contextmenu', this._contextmenuHandler)
    }
    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler)
    }
  }

  _cachedBoundsOptions = null

  getBoundsOptions () {
    const { boundsOptions } = this.props
    if (!boundsOptions) return boundsOptions
    const prev = this._cachedBoundsOptions
    if (
      prev &&
      prev.paddingTopLeft && boundsOptions.paddingTopLeft &&
      prev.paddingTopLeft.x === boundsOptions.paddingTopLeft.x &&
      prev.paddingTopLeft.y === boundsOptions.paddingTopLeft.y &&
      prev.paddingBottomRight && boundsOptions.paddingBottomRight &&
      prev.paddingBottomRight.x === boundsOptions.paddingBottomRight.x &&
      prev.paddingBottomRight.y === boundsOptions.paddingBottomRight.y
    ) {
      return prev
    }
    this._cachedBoundsOptions = boundsOptions
    return boundsOptions
  }

  render () {
    const boundsOptions = this.getBoundsOptions()
    return (
      <Map
        ref={this.mapRef}
        {...this.props}
        boundsOptions={boundsOptions}
      >
        {this.props.children}
      </Map>
    )
  }
}

export default CustomMap
